import { useRef, type ReactNode } from 'react';
import { hasFlatNineAvoidInterval, voicingIdentityLabel } from '@/music/voicing';
import { useHoldStrum } from '@/audio/useHoldStrum';
import type { DiagramMarkers } from '@/state/displayScale';
import { ChordDiagram } from './ChordDiagram';
import { useInspector, type InspectChord } from './inspectorContext';

interface PlayableDiagramProps {
  chord: InspectChord;
  caption?: ReactNode;
  showIntervals?: boolean;
  highlightAvoid?: boolean;
  /** Render the chord title inside the diagram (off when a header sits above). */
  showTitle?: boolean;
  fretNumberScale?: number;
  markers?: DiagramMarkers;
  /** 'card' wraps in a bordered card; 'bare' renders just the diagram. */
  variant?: 'card' | 'bare';
  className?: string;
}

// A second press within this window counts as a double-click → open the inspector.
const DOUBLE_MS = 280;

/**
 * A chord diagram you play and inspect like the strings themselves:
 *  - press (tap or hold) strums the chord using the hold-to-strum logic
 *    (quick = block chord, hold = a roll spanning the hold) — no fill animation;
 *  - double-click / double-tap opens the voicing inspector ("info box").
 */
export function PlayableDiagram({
  chord,
  caption,
  showIntervals = true,
  highlightAvoid = false,
  showTitle = true,
  fretNumberScale,
  markers,
  variant = 'card',
  className,
}: PlayableDiagramProps) {
  const { inspect } = useInspector();
  const { begin, end, cancel } = useHoldStrum(chord.fingering, chord.stringSet);
  const lastUpRef = useRef(0);
  const avoid = highlightAvoid && hasFlatNineAvoidInterval(chord.fingering, chord.stringSet);
  const label = `${chord.symbol} · ${voicingIdentityLabel(
    chord.fingering,
    chord.rootDisplay,
    chord.chordType,
  )}`;

  // Suppress the *second* press of a double-click from re-playing; the inspector
  // is opened reliably by the native onDoubleClick handler below.
  const handleUp = () => {
    const now = performance.now();
    if (now - lastUpRef.current < DOUBLE_MS) {
      lastUpRef.current = 0;
      cancel();
    } else {
      lastUpRef.current = now;
      end();
    }
  };

  const gesture = {
    role: 'button' as const,
    tabIndex: 0,
    title: 'Press to play · double-click for details',
    'aria-label': `${label} — press to play, double-click for details`,
    onPointerDown: (e: React.PointerEvent) => {
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      // Don't arm playback for the second press of a double-click — that gesture
      // opens the inspector, and arming would plant a stray bass note.
      if (performance.now() - lastUpRef.current < DOUBLE_MS) return;
      begin();
    },
    onPointerUp: handleUp,
    onPointerCancel: cancel,
    // A passage drag that starts on the grip moves the pointer capture to the
    // sheet, and the release then goes THERE — without this the press never
    // ended, and the next tap on the grip replayed the stale hold as a slow
    // bass-less roll. After an ordinary release the hold has already ended,
    // so the implicit capture loss that follows it changes nothing.
    onLostPointerCapture: cancel,
    onDoubleClick: () => {
      cancel();
      inspect(chord);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
        e.preventDefault();
        begin();
      }
    },
    onKeyUp: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        end();
      }
    },
  };

  const diagram = (
    <ChordDiagram
      fingering={chord.fingering}
      rootDisplay={chord.rootDisplay}
      chordType={chord.chordType}
      stringSet={chord.stringSet}
      title={chord.title ?? label}
      showTitle={showTitle}
      fretNumberScale={fretNumberScale}
      markers={markers}
      leadNote={chord.leadNote ?? null}
      showIntervals={showIntervals}
      highlightAvoid={highlightAvoid}
      ariaLabel={label}
    />
  );

  // `.chord-bare` is the sibling of `.chord-card` below — one class per
  // variant. It was `.progression-chord`, named after the page that used to be
  // its only consumer; once the Progressions page moved to the card, both
  // remaining consumers (the diagram sheet, the Top-note Finder) were wearing
  // the name of a page they are not on.
  if (variant === 'bare') {
    return (
      <div
        className={`chord-bare${className ? ` ${className}` : ''}`}
        data-pinned={chord.pin?.pinned || undefined}
        data-source-shape-id={chord.fingering.sourceShapeId}
        {...(chord.fingering.unavailable ? {} : gesture)}
      >
        {diagram}
      </div>
    );
  }

  // The b9 flag lives in the caption row, not over the fretboard — a corner
  // badge collides with high-fret dots now grid cards have no title band.
  const flag = avoid && (
    <span
      className="avoid-flag"
      title="Contains a minor-9th (b9) clash — the classic avoid interval"
    >
      b9
    </span>
  );

  return (
    <div
      className={`chord-card${avoid ? ' is-avoid' : ''}${className ? ` ${className}` : ''}`}
      data-source-shape-id={chord.fingering.sourceShapeId}
      {...(chord.fingering.unavailable ? {} : gesture)}
    >
      {diagram}
      {(caption || flag) && (
        <div className="card-caption">
          {caption}
          {flag}
        </div>
      )}
    </div>
  );
}
